import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  useDocumentTypeService,
  usePhoneCountryCodeService,
  useUserOperatorAssignmentsService,
  useUserVehicleAssignmentsService,
} from "../service/userComplementaryService";
import { useMemo, useCallback } from "react";

export const DOCUMENT_TYPES_KEY = "documentTypes";
export const PHONE_COUNTRY_CODES_KEY = "phoneCountryCodes";
export const useDocumentTypes = () => {
  const documentTypeService = useDocumentTypeService();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [DOCUMENT_TYPES_KEY],
    queryFn: () => documentTypeService.getAllDocumentTypes(),
    staleTime: 3 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchInterval: 5 * 60 * 1000,
    retry: 2,
  });

  // Memorizamos los documentTypes para evitar re-renders innecesarios
  const memoizedDocumentTypes = useMemo(() => {
    return query.data?.data || [];
  }, [query.data]);

  const createMutation = useMutation({
    mutationFn: (data) => documentTypeService.createDocumentType(data),
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENT_TYPES_KEY]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => documentTypeService.updateDocumentType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENT_TYPES_KEY]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => documentTypeService.deleteDocumentType(id),
    onSuccess: () => {
      queryClient.invalidateQueries([DOCUMENT_TYPES_KEY]);
    },
  });

  return {
    ...query,
    documentTypes: memoizedDocumentTypes,
    createDocumentType: createMutation.mutateAsync,
    updateDocumentType: updateMutation.mutateAsync,
    deleteDocumentType: deleteMutation.mutateAsync,
    isLoading: query.isLoading || createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading,
    isError: query.isError || createMutation.isError || updateMutation.isError || deleteMutation.isError,
  };
};
export const usePhoneCountryCodes = () => {
    const phoneCountryCodeService = usePhoneCountryCodeService();
    const queryClient = useQueryClient();
  
    const query = useQuery({
      queryKey: [PHONE_COUNTRY_CODES_KEY],
      queryFn: () => phoneCountryCodeService.getAllPhoneCountryCodes(),
      staleTime: 3 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchInterval: 5 * 60 * 1000,
      retry: 2,
    });
    const memoizedCountryCodes = useMemo(() => {
      return query.data?.data || [];
    }, [query.data]);

    const createMutation = useMutation({
      mutationFn: (data) => phoneCountryCodeService.createPhoneCountryCode(data),
      onSuccess: () => {
        queryClient.invalidateQueries([PHONE_COUNTRY_CODES_KEY]);
      },
    });
  
    const updateMutation = useMutation({
      mutationFn: ({ id, data }) => phoneCountryCodeService.updatePhoneCountryCode(id, data),
      onSuccess: () => {
        queryClient.invalidateQueries([PHONE_COUNTRY_CODES_KEY]);
      },
    });
  
    const deleteMutation = useMutation({
      mutationFn: (id) => phoneCountryCodeService.deletePhoneCountryCode(id),
      onSuccess: () => {
        queryClient.invalidateQueries([PHONE_COUNTRY_CODES_KEY]);
      },
    });
  
    return {
      ...query,
      countryCodes: memoizedCountryCodes,
      createPhoneCountryCode: createMutation.mutate,
      updatePhoneCountryCode: updateMutation.mutate,
      deletePhoneCountryCode: deleteMutation.mutate,
      isLoading: query.isLoading || createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading,
      isError: query.isError || createMutation.isError || updateMutation.isError || deleteMutation.isError,
    };
  };

// ─── Hooks: Asignaciones de operadores a gestores CUV/LUV ─────────────────

export const USER_OP_ASSIGN_MANAGERS_KEY = "uoaManagers";
export const USER_OP_ASSIGN_OPERATORS_KEY = "uoaOperators";
export const USER_OP_ASSIGN_AVAILABLE_KEY = "uoaAvailable";

/** Lista todos los gestores CUV/LUV */
export const useUOAManagers = () => {
  const svc = useUserOperatorAssignmentsService();
  return useQuery({
    queryKey: [USER_OP_ASSIGN_MANAGERS_KEY],
    queryFn: () => svc.getManagers(),
    staleTime: 3 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    select: useCallback((data) => data?.data || [], []),
  });
};

/** Operadores asignados a un gestor (paginado) */
export const useUOAOperatorsByManager = (managerId, { searchTerm = "", page = 1, limit = 10 } = {}) => {
  const svc = useUserOperatorAssignmentsService();
  return useQuery({
    queryKey: [USER_OP_ASSIGN_OPERATORS_KEY, managerId, searchTerm, page, limit],
    queryFn: () => svc.getOperatorsByManager(managerId, { searchTerm, page, limit }),
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!managerId,
    keepPreviousData: true,
  });
};

/** Operadores disponibles (no asignados aún) para un gestor */
export const useUOAAvailableOperators = (managerId, searchTerm = "") => {
  const svc = useUserOperatorAssignmentsService();
  return useQuery({
    queryKey: [USER_OP_ASSIGN_AVAILABLE_KEY, managerId, searchTerm],
    queryFn: () => svc.getAvailableOperators(managerId, searchTerm),
    staleTime: 1 * 60 * 1000,
    cacheTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!managerId,
    select: useCallback((data) => data?.data || [], []),
  });
};

/** Mutaciones: asignar, asignar lote, remover, remover lote */
export const useUOAMutations = () => {
  const svc = useUserOperatorAssignmentsService();
  const queryClient = useQueryClient();

  const invalidate = (managerId) => {
    queryClient.invalidateQueries([USER_OP_ASSIGN_OPERATORS_KEY, managerId]);
    queryClient.invalidateQueries([USER_OP_ASSIGN_AVAILABLE_KEY, managerId]);
    queryClient.invalidateQueries([USER_OP_ASSIGN_MANAGERS_KEY]);
  };

  const assignMutation = useMutation({
    mutationFn: ({ managerId, operatorId }) => svc.assign(managerId, operatorId),
    onSuccess: (_, vars) => invalidate(vars.managerId),
  });

  const assignBatchMutation = useMutation({
    mutationFn: ({ managerId, operatorIds }) => svc.assignBatch(managerId, operatorIds),
    onSuccess: (_, vars) => invalidate(vars.managerId),
  });

  const removeMutation = useMutation({
    mutationFn: ({ managerId, operatorId }) => svc.remove(managerId, operatorId),
    onSuccess: (_, vars) => invalidate(vars.managerId),
  });

  const removeBatchMutation = useMutation({
    mutationFn: ({ managerId, operatorIds }) => svc.removeBatch(managerId, operatorIds),
    onSuccess: (_, vars) => invalidate(vars.managerId),
  });

  return {
    assign: assignMutation.mutateAsync,
    assignBatch: assignBatchMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    removeBatch: removeBatchMutation.mutateAsync,
    isAssigning: assignMutation.isLoading || assignBatchMutation.isLoading,
    isRemoving: removeMutation.isLoading || removeBatchMutation.isLoading,
  };
};

// ─── Hooks: Asignaciones de vehículos (cabezotes) a gestores CUV/LUV ────────────────────

export const USER_VEH_ASSIGN_MANAGERS_KEY = "uvaManagers";
export const USER_VEH_ASSIGN_VEHICLES_KEY = "uvaVehicles";
export const USER_VEH_ASSIGN_AVAILABLE_KEY = "uvaAvailable";

/** Lista todos los gestores CUV/LUV con conteo de vehículos asignados */
export const useUVAManagers = () => {
  const svc = useUserVehicleAssignmentsService();
  return useQuery({
    queryKey: [USER_VEH_ASSIGN_MANAGERS_KEY],
    queryFn: () => svc.getManagers(),
    staleTime: 3 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    select: useCallback((data) => data?.data || [], []),
  });
};

/** Vehículos asignados a un gestor (paginado) */
export const useUVAVehiclesByManager = (managerId, { searchTerm = "", page = 1, limit = 10 } = {}) => {
  const svc = useUserVehicleAssignmentsService();
  return useQuery({
    queryKey: [USER_VEH_ASSIGN_VEHICLES_KEY, managerId, searchTerm, page, limit],
    queryFn: () => svc.getVehiclesByManager(managerId, { searchTerm, page, limit }),
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!managerId,
    keepPreviousData: true,
  });
};

/** Vehículos disponibles (cabezotes no asignados aún) para un gestor */
export const useUVAAvailableVehicles = (managerId, searchTerm = "") => {
  const svc = useUserVehicleAssignmentsService();
  return useQuery({
    queryKey: [USER_VEH_ASSIGN_AVAILABLE_KEY, managerId, searchTerm],
    queryFn: () => svc.getAvailableVehicles(managerId, searchTerm),
    staleTime: 1 * 60 * 1000,
    cacheTime: 3 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!managerId,
    select: useCallback((data) => data?.data || [], []),
  });
};

/** Mutaciones: asignar, asignar lote, remover, remover lote (vehículos) */
export const useUVAMutations = () => {
  const svc = useUserVehicleAssignmentsService();
  const queryClient = useQueryClient();

  const invalidate = (managerId) => {
    queryClient.invalidateQueries([USER_VEH_ASSIGN_VEHICLES_KEY, managerId]);
    queryClient.invalidateQueries([USER_VEH_ASSIGN_AVAILABLE_KEY, managerId]);
    queryClient.invalidateQueries([USER_VEH_ASSIGN_MANAGERS_KEY]);
  };

  const assignBatchMutation = useMutation({
    mutationFn: ({ managerId, vehicleIds }) => svc.assignBatch(managerId, vehicleIds),
    onSuccess: (_, vars) => invalidate(vars.managerId),
  });

  const removeMutation = useMutation({
    mutationFn: ({ managerId, vehicleId }) => svc.remove(managerId, vehicleId),
    onSuccess: (_, vars) => invalidate(vars.managerId),
  });

  return {
    assignBatch: assignBatchMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    isAssigning: assignBatchMutation.isLoading,
    isRemoving: removeMutation.isLoading,
  };
};

// ─── Hooks de sesión: operadores y vehículos permitidos para el usuario CUV/LUV activo ─────────
// Se usan en AuthContext para poblar allowedOperators y allowedVehicles.
// Renuevan la info cada 10 minutos mientras la app esté abierta.

export const MY_ALLOWED_OPERATORS_KEY = "myAllowedOperators";
export const MY_ALLOWED_VEHICLES_KEY = "myAllowedVehicles";

/**
 * Trae TODOS los operadores asignados al usuario autenticado como gestor CUV/LUV.
 * Solo activo cuando managerId tiene valor.
 * Refresca automáticamente cada 10 minutos.
 */
export const useMyAllowedOperators = (managerId) => {
  const svc = useUserOperatorAssignmentsService();
  return useQuery({
    queryKey: [MY_ALLOWED_OPERATORS_KEY, managerId],
    queryFn: () => svc.getOperatorsByManager(managerId, { searchTerm: "", page: 1, limit: 9999 }),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!managerId,
    select: useCallback((data) => data?.data ?? [], []),
  });
};

/**
 * Trae TODOS los vehículos (cabezotes) asignados al usuario autenticado como gestor CUV/LUV.
 * Solo activo cuando managerId tiene valor.
 * Refresca automáticamente cada 10 minutos.
 */
export const useMyAllowedVehicles = (managerId) => {
  const svc = useUserVehicleAssignmentsService();
  return useQuery({
    queryKey: [MY_ALLOWED_VEHICLES_KEY, managerId],
    queryFn: () => svc.getVehiclesByManager(managerId, { searchTerm: "", page: 1, limit: 9999 }),
    staleTime: 10 * 60 * 1000,
    cacheTime: 15 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!managerId,
    select: useCallback((data) => data?.data ?? [], []),
  });
};